import React from 'react';
import { Remote } from '@okapi/remote';
import { useSelector } from 'react-redux';
import { getRemoteTimeout } from 'common/selectors';
import { APP_MAIN_ID, DISABLE_LOYALTY_REWARDS_EXPERIMENT } from 'common/constants';
import { createGetExperiment, getDivision, getFamily, getSubdivision } from '../../page-model';
import { getRegionName } from 'features/page-model/ducks/get-model';
import { css, cx } from 'linaria';
import { RemoteData } from '../types';

export type ProductCarouselProps = {
  products?: string[];
  title?: string;
  carouselId?: string;
  className?: string;
  placementId?: string;
  productId?: string;
  limitMax?: number;
  variant?: 'default' | 'sponsored';
};

export const ProductCarousel: React.FC<ProductCarouselProps> = ({
  products,
  title = '',
  carouselId = '',
  className,
  placementId,
  productId,
  limitMax,
  variant = 'default',
}) => {
  const remoteTimeout = useSelector(getRemoteTimeout);
  const disableLoyaltyRewards = useSelector(createGetExperiment(DISABLE_LOYALTY_REWARDS_EXPERIMENT));
  const productFamilyId = useSelector(getFamily);
  const division = useSelector(getDivision);
  const subdivision = useSelector(getSubdivision);
  const regionName = useSelector(getRegionName);

  const analytics = { division, subdivision, productFamilyId };

  const remoteData: RemoteData = {
    title: title,
    productsIds: products ?? [],
    showSeeMore: false,
    isFromPdp: true,
    useAllModal: true,
    elementBlock: carouselId,
    portalRootId: APP_MAIN_ID,
    showLoyaltyRewards: !disableLoyaltyRewards,
    analytics,
  };

  if (variant === 'sponsored') {
    remoteData.className = sponsoredWrapper;
    remoteData.isSimplifiedVariant = true;
  }

  if (placementId) {
    Object.assign(remoteData, {
      subtitle: 'Промо',
      sponsored: {
        placementId,
        pageType: 'productPlacements',
        acceptContent: 'sharedProductIds',
        productId: productId ?? null,
        limitMax: limitMax ?? null,
        regionName: regionName ?? null,
      },
    });
  }

  const widget = variant === 'sponsored' ? 'product-carousel/sponsored' : 'product-carousel/root';
  const wrapperStyle = variant === 'sponsored' ? sponsoredWrapper : defaultWrapper;

  return (
    <Remote
      widget={widget}
      id={'pdp-' + carouselId}
      data={remoteData}
      className={cx(className, wrapperStyle)}
      serverTimeout={remoteTimeout}
    />
  );
};

const defaultWrapper = css`
  @media (--only-mobile) {
    [data-qa='product-image'] {
      width: 100% !important;
      height: auto !important;
    }
  }
`;

const sponsoredWrapper = css`
  @media (--from-desktop) {
    width: 694px !important;
  }

  @media screen and (min-width: 1024px) and (max-width: 1199px) {
    width: 578px !important;
  }

  @media (--only-tablet) {
    & > div {
      width: 100vw;
      max-width: none;
      padding-right: var(--space-625);
    }
  }
`;
